// M7 films-hyperlinks — agent-side editor for the player's highlight reel
// URLs. Controlled component: the parent (PlayerEdit) owns the rows and
// persists them via the page's single "Save changes" submit, so there is
// NO save button here.
//
// History: this form used to own its rows + a dedicated "Save films"
// button. An agent who edited films and clicked only the page's main
// "Save changes" silently lost those edits — the bug this unification
// fixes. Films now flow up via onChange and save with everything else.
//
// Replace semantics live on the server: PUT /api/players/:id deletes the
// player's existing films and inserts whatever the parent sends. The cap
// is server-enforced; the UI disables "Add film" at the cap to match.
//
// Title is optional, but 2026-07-30D made it the point of the feature —
// the public profile is now a labelled card grid ("30 points versus
// Croatia"), so an untitled clip falls back to a bare "Film 1" / "Film 2".
// The form nudges toward filling it in without requiring it.

import { Trash2, Plus, Film } from 'lucide-react'

// Mirror of MAX_FILMS in functions/api/players/[id].ts — that copy is the
// enforced one (400 too_many_films). The client bundle can't import from
// functions/, so these are synced by hand. Change both or the form will
// happily build a payload the server rejects.
export const MAX_FILMS = 9
export const MAX_TITLE_LEN = 80

export interface FilmInputRow {
  url: string
  title: string
}

interface Props {
  rows: FilmInputRow[]
  onChange: (rows: FilmInputRow[]) => void
}

// Local-only per-row validation for inline feedback. The server is the
// source of truth and re-validates every row on save.
function rowError(row: FilmInputRow): string | null {
  const url = row.url.trim()
  if (!url) return 'URL required'
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return 'Invalid URL'
  }
  if (parsed.protocol !== 'https:') return 'Must be https://'
  if (row.title.trim().length > MAX_TITLE_LEN) {
    return `Title over ${MAX_TITLE_LEN} chars`
  }
  return null
}

export default function FilmsForm({ rows, onChange }: Props) {
  function updateRow(idx: number, patch: Partial<FilmInputRow>) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  function addRow() {
    if (rows.length >= MAX_FILMS) return
    onChange([...rows, { url: '', title: '' }])
  }

  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx))
  }

  const perRowErrors = rows.map((r) => rowError(r))

  return (
    <section className="rounded-lg border border-hoop-border bg-hoop-bg p-5">
      <header className="flex items-center gap-2 mb-1">
        <Film className="w-4 h-4 text-hoop-text-2" />
        <h2 className="text-sm font-semibold text-hoop-text">
          Film
        </h2>
      </header>
      <p className="text-xs text-hoop-text-3 mb-4">
        Hyperlinks to highlight reels — YouTube, Google Drive, Instagram,
        Facebook, anywhere. Up to {MAX_FILMS}. Title optional but recommended
        — coaches see these as a grid of labelled cards, and an untitled clip
        just reads &ldquo;Film 1&rdquo;. Saved with{' '}
        <span className="font-medium">Save changes</span> below.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs text-hoop-text-3 mb-3 italic">
          No films yet.
        </p>
      ) : (
        <ul className="space-y-3 mb-3">
          {rows.map((r, idx) => {
            const error = perRowErrors[idx]
            return (
              <li
                key={idx}
                className="rounded-md border border-hoop-border bg-hoop-surface p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-hoop-text-3 mb-0.5">
                        URL
                      </label>
                      <input
                        type="url"
                        inputMode="url"
                        autoComplete="off"
                        value={r.url}
                        onChange={(e) => updateRow(idx, { url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=…"
                        className="w-full rounded-md border border-hoop-border bg-hoop-surface px-2.5 py-1.5 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-hoop-text-3 mb-0.5">
                        Title <span className="normal-case tracking-normal text-hoop-text-3">(optional)</span>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        maxLength={MAX_TITLE_LEN + 10}
                        value={r.title}
                        onChange={(e) => updateRow(idx, { title: e.target.value })}
                        placeholder="e.g. 30 points versus Croatia"
                        className="w-full rounded-md border border-hoop-border bg-hoop-surface px-2.5 py-1.5 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    aria-label={`Remove film ${idx + 1}`}
                    className="shrink-0 text-hoop-text-3 hover:text-hoop-red p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {error && (
                  <p className="mt-1.5 text-[11px] text-hoop-red">
                    {error}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={addRow}
        disabled={rows.length >= MAX_FILMS}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-hoop-text-2 hover:text-hoop-text disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-3.5 h-3.5" />
        Add film
        {rows.length >= MAX_FILMS && (
          <span className="font-normal text-hoop-text-3">
            (max {MAX_FILMS})
          </span>
        )}
      </button>
    </section>
  )
}
